// apps/admin/src/application/staff/staff_repository.ts
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import { hashStaffPin, newUuid } from "@cpos/shared";
import {
  DEFAULT_STAFF_PERMISSIONS,
  type StaffCreateInput,
  type StaffPermissions,
  type StaffRow,
  type StaffUpdateInput,
} from "./types";

const client = generateClient<Schema>();

// ── Secondary-index cast helper ───────────────────────────────────────────────
// Amplify Gen 2 generates listBy* methods from secondary indexes at runtime but
// does not expose them in the generated TypeScript types. Access via narrowly-
// scoped cast, mirroring the pattern in money_repository.ts.

interface StaffRecord {
  id: string;
  name: string;
  email?: string | null;
  isOwner: boolean;
  isActive: boolean;
  permissions?: unknown;
}

type ListByResult = Promise<{
  data: StaffRecord[];
  nextToken?: string | null;
  errors?: { message: string }[];
}>;

interface ListByOpts {
  nextToken?: string | null;
}

function listStaffByBusinessIdAndName(
  key: { businessId: string },
  opts: ListByOpts,
): ListByResult {
  const model = client.models.Staff as unknown as {
    listStaffByBusinessIdAndName: (
      key: { businessId: string },
      opts: ListByOpts,
    ) => ListByResult;
  };
  return model.listStaffByBusinessIdAndName(key, opts);
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function normalizePermissions(raw: unknown): StaffPermissions {
  const p = (raw ?? {}) as Partial<StaffPermissions>;
  return {
    canProcessPayments: p.canProcessPayments ?? false,
    canVoid: p.canVoid ?? false,
    canRefund: p.canRefund ?? false,
    canViewSalesSummary: p.canViewSalesSummary ?? false,
    canManageStaff: p.canManageStaff ?? false,
    canAccessSettings: p.canAccessSettings ?? false,
  };
}

// ── Repository ────────────────────────────────────────────────────────────────

export class StaffRepository {
  async listByBusiness(businessId: string): Promise<StaffRow[]> {
    const rows: StaffRow[] = [];
    let nextToken: string | null = null;
    for (;;) {
      const page = await listStaffByBusinessIdAndName(
        { businessId },
        { nextToken },
      );
      if (page.errors?.length) throw new Error(page.errors[0].message);
      for (const s of page.data ?? []) {
        rows.push({
          id: s.id,
          name: s.name,
          email: s.email ?? null,
          isOwner: s.isOwner,
          isActive: s.isActive,
          permissions: normalizePermissions(s.permissions),
        });
      }
      if (!page.nextToken) break;
      nextToken = page.nextToken;
    }
    return rows;
  }

  async create(businessId: string, input: StaffCreateInput): Promise<void> {
    const { data, errors } = await client.mutations.CreateStaffMember({
      businessId,
      staffId: newUuid(),
      name: input.name.trim(),
      pinHash: await hashStaffPin(input.pin),
      isOwner: input.isOwner,
      isActive: input.isActive,
      email: input.email?.trim() || undefined,
      permissions: input.permissions ?? DEFAULT_STAFF_PERMISSIONS,
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data?.success)
      throw new Error(data?.error ?? "Failed to create staff");
  }

  async update(businessId: string, input: StaffUpdateInput): Promise<void> {
    const { data, errors } = await client.mutations.UpdateStaffMember({
      businessId,
      staffId: input.staffId,
      name: input.name?.trim(),
      pinHash: input.pin ? await hashStaffPin(input.pin) : undefined,
      isOwner: input.isOwner,
      isActive: input.isActive,
      email:
        input.email === undefined
          ? undefined
          : input.email?.trim() || undefined,
      permissions: input.permissions,
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data?.success)
      throw new Error(data?.error ?? "Failed to update staff");
  }
}
